// src/Components/ViewInfo/DocsView.js

import React, { useState } from 'react';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import { MenuDownload } from '../../assets/icons/CustomIcons';

const ICON_SIZE = 64;

const styles = {
  label: {
    color: '#00008B',
    fontWeight: 'bold',
    fontSize: '0.65rem',
    textTransform: 'uppercase',
    marginBottom: '0px',
    lineHeight: 1.2,
    // --- FIX: Explicit Font stack ---
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
  },
  valueBox: {
    borderRadius: '4px',
    minHeight: '24px',
    display: 'flex',
    alignItems: 'center',
    padding: '4px 8px',
    overflow: 'hidden',
    backgroundColor: '#fff',
    border: '1px solid orange',
    width: '100%',
  },
  checkboxBox: {
    borderRadius: '4px',
    width: '30px',
    height: '30px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#fff',
    border: '1px solid orange',
    cursor: 'pointer',
  },
  text: {
    fontSize: '0.8rem',
    color: '#333',
    padding: 0,
    margin: 0,
    cursor: 'default',
    // --- FIX: Explicit Font stack ---
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
  },
  checkbox: {
    padding: 0,
    '&.Mui-checked': {
      color: '#00008B',
    },
  },
};

export default function DocsView({ showAlert, docs }) {
  const [selectedIds, setSelectedIds] = useState(new Set());
  const docList = docs || [];

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      setSelectedIds(new Set(docList.map((item) => item.id)));
    } else {
      setSelectedIds(new Set());
    }
  };

  const handleRowClick = (event, id) => {
    event.stopPropagation();
    const newSelected = new Set(selectedIds);
    if (newSelected.has(id)) newSelected.delete(id);
    else newSelected.add(id);
    setSelectedIds(newSelected);
  };

  const downloadFile = async (url, fileName) => {
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = fileName || 'download';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error('Download failed:', error);
    }
  };

  const handleDownload = async () => {
    if (selectedIds.size === 0) return;
    if (showAlert)
      showAlert('info', `Starting download for ${selectedIds.size} file(s)...`);
    const docsToDownload = docList.filter((doc) => selectedIds.has(doc.id));
    for (const doc of docsToDownload) {
      if (doc.url) await downloadFile(doc.url, doc.fileName);
    }
    if (showAlert) setTimeout(() => showAlert('success', 'Downloads completed.'), 500);
  };

  const isAllSelected = docList.length > 0 && selectedIds.size === docList.length;
  const isIndeterminate = selectedIds.size > 0 && selectedIds.size < docList.length;

  return (
    <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* SUB-TOOLBAR AREA */}
      <Box
        sx={{
          height: '60px',
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#f4f4f4',
          borderBottom: '1px solid #ccc',
          flexShrink: 0,
        }}>
        <Tooltip title='Download Selected'>
          <span>
            <IconButton
              onClick={handleDownload}
              disabled={selectedIds.size === 0}
              sx={{ p: 0, transform: 'translateY(-5px)' }}>
              <MenuDownload
                sx={{
                  fontSize: ICON_SIZE,
                  opacity: selectedIds.size === 0 ? 0.35 : 1,
                  filter: selectedIds.size === 0 ? 'grayscale(100%)' : 'none',
                }}
                iconScale={5}
                iconX={32}
                iconY={38}
                textY={108}
              />
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      {/* TABLE CONTENT */}
      <Box sx={{ flexGrow: 1, p: 1, overflow: 'auto' }}>
        <TableContainer
          component={Paper}
          elevation={0}
          sx={{ backgroundColor: 'transparent' }}>
          <Table
            size='small'
            stickyHeader
            sx={{ borderCollapse: 'separate', borderSpacing: '0 4px' }}>
            <TableHead>
              <TableRow>
                <TableCell
                  padding='checkbox'
                  sx={{ width: '40px', borderBottom: 'none', pb: 0 }}>
                  <Checkbox
                    indeterminate={isIndeterminate}
                    checked={isAllSelected}
                    onChange={handleSelectAll}
                    size='small'
                    sx={styles.checkbox}
                    disabled={docList.length === 0}
                  />
                </TableCell>
                <TableCell sx={{ borderBottom: 'none', pb: 0, pl: 1 }}>
                  <Typography sx={styles.label}>DOCUMENT NAME</Typography>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {docList.map((row) => {
                const isSelected = selectedIds.has(row.id);
                return (
                  <TableRow
                    key={row.id}
                    hover
                    onClick={(event) => handleRowClick(event, row.id)}
                    selected={isSelected}
                    sx={{ cursor: 'pointer', '& td': { borderBottom: 'none' } }}>
                    <TableCell padding='none'>
                      <Box sx={{ display: 'flex', justifyContent: 'center', pl: '4px' }}>
                        <Box sx={styles.checkboxBox}>
                          <Checkbox
                            checked={isSelected}
                            size='small'
                            sx={styles.checkbox}
                          />
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ pl: 1 }}>
                      <Box sx={styles.valueBox}>
                        <Typography sx={styles.text} noWrap>
                          {row.fileName || 'Untitled'}
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </Box>
  );
}
